import React, { useCallback, useEffect, useState } from 'react';

import { useCRMHost } from '../CRMHostContext';
import type { EarningPolicy } from '../crmClient';
import { toAuditEntries } from '../adapters';
import type { AuditEntry } from '../../components';
import { ScreenSkeleton } from '../../components';

import { PointRuleScreen } from '../../screens/PointRuleScreen';
import { CURRENT_POINT_RULE } from '../../screens/loyaltyData';
import type { PointRule } from '../../screens/loyaltyData';

/**
 * ต่อหน้ากติกาคะแนนเข้ากับ CRM module
 *
 * CRM เก็บกติกาเป็น "ตัวคูณ" และแยกคะแนนสองชนิด (tier / reward)
 * ส่วนหน้าจอเราออกแบบตาม SRS 3.4 ซึ่งมีเรื่องรอบรีเซ็ต ส่วนลดแรกเข้า ฯลฯ
 *
 * ⚠️ เจ็ดค่าที่ SRS กำหนดแต่ CRM ยังไม่มีที่เก็บ — แสดงเป็นคำเตือนบนหน้าจอ
 *    เพื่อไม่ให้คนตั้งค่าแก้แล้วเข้าใจผิดว่าบันทึกได้
 */
const UNSUPPORTED = [
  'ปัดเศษยอดที่ไม่ครบฐานลง',
  'เก็บเศษบาทไว้รวมกับรอบถัดไป',
  'แจ้งเตือนล่วงหน้าก่อนคะแนนหมดอายุ',
  'วิธีนับวันหมดอายุ (FIFO / ยกล็อต)',
  'รอบรีเซ็ตยอดสะสม',
  'นโยบายและระยะผ่อนผันเมื่อรีเซ็ต',
  'ส่วนลดแรกเข้า',
];

/** ช่วงที่นับว่า "ใกล้หมดอายุ" ในแถบสรุป — ตรงกับป้ายกำกับที่หน้าจอแสดง */
const EXPIRE_SOON_DAYS = 90;

export function PointRuleContainer() {
  const { api, dataMode, linkComponent } = useCRMHost();
  const isMock = dataMode === 'mock';

  const [rule, setRule] = useState<PointRule | undefined>(undefined);
  const [policyId, setPolicyId] = useState<number | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  /**
   * ประวัติการแก้กติกาคะแนน — undefined = ยังไม่โหลด/โหลดไม่สำเร็จ
   * ถ้าล้มเหลวไม่ควรทำให้ทั้งหน้าพัง เพราะประวัติเป็นของเสริม ไม่ใช่ของหลัก
   */
  const [auditEntries, setAuditEntries] = useState<AuditEntry[] | undefined>(undefined);
  /** คะแนนคงค้างจริงทั้ง division — undefined = ยังไม่ได้ค่า ใช้ตัวเลขจำลองแทน */
  const [impact, setImpact] = useState<{
    totalOutstandingPoints: number;
    membersWithBalance: number;
    pointsExpiringSoon: number;
    expireDays: number;
  } | undefined>(undefined);

  useEffect(() => {
    if (isMock) { setState('ready'); return; }
    let cancelled = false;
    setState('loading');
    Promise.all([
      api.listEarningPolicies(),
      /**
       * ตัวเลขคงค้าง = Point Liability (FR-RPT-06)
       *
       * ⚠️ เป็นของเสริมของหน้านี้ ถ้าล้มเหลวไม่ควรทำให้ตั้งกติกาไม่ได้
       *    จับ error แยกแล้วปล่อยให้แถบสรุปใช้ตัวเลขจำลองไปก่อน
       */
      api.getPointSummary(EXPIRE_SOON_DAYS).catch(() => null),
      /* ประวัติการแก้ไข — ของเสริมเช่นกัน ล้มแล้วหน้ายังต้องใช้งานได้ */
      api.listAuditLogs('earning-policy').catch(() => null),
    ])
      .then(([page, summary, audit]) => {
        if (cancelled) return;
        /* ใช้นโยบายสำรอง (default) เป็นตัวหลัก — เป็นตัวที่มีผลเมื่อไม่มีช่วงเวลาไหนตรง */
        const active = page.items.find((p) => p.default) ?? page.items[0];
        if (!active) { setState('error'); return; }
        setPolicyId(active.id);
        setRule(toPointRuleShape(active));
        setAuditEntries(audit ? toAuditEntries(audit.items) : undefined);
        if (summary) {
          setImpact({
            totalOutstandingPoints: Number(summary.totalPoints),
            membersWithBalance: summary.totalAccounts,
            pointsExpiringSoon: Number(summary.expiringPoints),
            expireDays: summary.expireDays,
          });
        }
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, []);

  const onSave = useCallback(async (next: PointRule) => {
    if (isMock || policyId === null) return;
    /* ส่งเฉพาะสองค่าที่ CRM มีที่เก็บจริง ที่เหลือถูกเตือนไว้บนหน้าจอแล้ว */
    const updated = await api.updateEarningPolicy(policyId, {
      rewardMultiplier: (next.pointsEarned / next.spendPerPoint).toFixed(3),
      rewardDurationDays: next.pointExpiryMonths === null ? 36_500 : Math.round(next.pointExpiryMonths * 30.44),
    });
    setRule(toPointRuleShape(updated));
  }, [policyId]);

  if (isMock) return <PointRuleScreen initialState="ready" linkComponent={linkComponent} />;

  /* ⚠️ เหตุผลเดียวกับหน้าระดับสมาชิก — ฟอร์มที่ขึ้นค่าจำลองระหว่างโหลดอ่านเหมือนค่าจริง
     และถ้าเผลอกดบันทึกตอนนั้นจะเขียนทับกติกาจริงของ division */
  if (state === 'loading' && !rule) {
    return <ScreenSkeleton title="กติกาการสะสมคะแนน" blocks={2} />;
  }

  return (
    <PointRuleScreen
      linkComponent={linkComponent}
      auditEntries={auditEntries ?? []}
      initialState={state}
      rule={rule}
      onSave={onSave}
      unsupportedFields={UNSUPPORTED}
      impact={impact ?? null}
    />
  );
}

/**
 * CRM → รูปแบบที่ PointRuleScreen ใช้
 *
 * ค่าที่ CRM ไม่มี ใช้ค่าตั้งต้นจาก SRS ไปก่อน และถูกทำเครื่องหมายว่าแก้ไม่ได้บนหน้าจอ
 */
function toPointRuleShape(p: EarningPolicy): PointRule {
  const rm = Number(p.rewardMultiplier);
  return {
    ...CURRENT_POINT_RULE,
    /* ตัวคูณ 0.01 = ซื้อ 100 บาทได้ 1 คะแนน */
    spendPerPoint: rm > 0 ? Math.round(1 / rm) : CURRENT_POINT_RULE.spendPerPoint,
    pointsEarned: 1,
    pointExpiryMonths: p.rewardDurationDays >= 36_500
      ? null
      : Math.round(p.rewardDurationDays / 30.44),
  };
}
